import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlass, User, Funnel, CaretLeft, CaretRight, WifiHigh, MapPin, ArrowDown, ArrowUp, Headset } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

function DMACustomersTab() {
  const navigate = useNavigate();
  const [data, setData] = useState({ total: 0, page: 1, pages: 0, data: [] });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [services, setServices] = useState([]);
  const [serviceFilter, setServiceFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 50 });
      if (search) params.append('search', search);
      if (status !== 'all') params.append('status', status);
      if (serviceFilter !== 'all') params.append('service', serviceFilter);
      const res = await fetch(`${API}/customers?${params}`);
      if (res.ok) setData(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [page, status, serviceFilter]);
  useEffect(() => { fetch(`${API}/dma/services`).then(r => r.json()).then(setServices).catch(() => {}); }, []);

  const handleSearch = (e) => { e.preventDefault(); setPage(1); load(); };
  const formatBytes = (b) => { if (!b) return '0'; const k = 1024; const u = ['B','KB','MB','GB','TB']; const i = Math.floor(Math.log(Math.abs(b)||1)/Math.log(k)); return `${(b/Math.pow(k,i)).toFixed(1)} ${u[i]}`; };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <form onSubmit={handleSearch} className="relative flex-1 max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--md-on-surface-variant)]" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search username, name, phone..." className="md3-input pl-9 h-10" />
        </form>
        <Select value={status} onValueChange={v => { setStatus(v); setPage(1); }}>
          <SelectTrigger className="w-32 bg-[var(--md-surface-1)] border-[var(--md-outline-variant)] rounded-xl h-10"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="all">All Status</SelectItem><SelectItem value="active">Active</SelectItem><SelectItem value="expired">Expired</SelectItem></SelectContent>
        </Select>
        <Select value={serviceFilter} onValueChange={v => { setServiceFilter(v); setPage(1); }}>
          <SelectTrigger className="w-40 bg-[var(--md-surface-1)] border-[var(--md-outline-variant)] rounded-xl h-10"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="all">All Services</SelectItem>{services.map(s => <SelectItem key={s.srvid} value={s.srvname}>{s.srvname}</SelectItem>)}</SelectContent>
        </Select>
        <span className="text-xs text-[var(--md-on-surface-variant)] tabular-nums">{data.total.toLocaleString()} customers</span>
      </div>
      <div className="md3-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="bg-[var(--md-surface-1)]">
              <th className="text-left text-[10px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2.5">Username</th>
              <th className="text-left text-[10px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2.5">Name</th>
              <th className="text-left text-[10px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2.5 hidden md:table-cell">Phone</th>
              <th className="text-left text-[10px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2.5">Service</th>
              <th className="text-left text-[10px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2.5 hidden lg:table-cell">MAC</th>
              <th className="text-left text-[10px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2.5 hidden lg:table-cell">IP</th>
              <th className="text-left text-[10px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2.5">Status</th>
            </tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={7} className="text-center py-8"><div className="w-6 h-6 border-2 border-[var(--md-primary)] border-t-transparent rounded-full animate-spin mx-auto" /></td></tr> :
              data.data.length === 0 ? <tr><td colSpan={7} className="text-center py-8 text-sm text-[var(--md-on-surface-variant)]">No customers found</td></tr> :
              data.data.map((c, i) => (
                <tr key={i} onClick={() => navigate(`/customers/${c.username}`)} className="border-t border-[var(--md-outline-variant)] md3-table-row cursor-pointer">
                  <td className="px-4 py-2.5 text-sm font-medium text-[var(--md-primary)] tabular-nums">{c.username}</td>
                  <td className="px-4 py-2.5 text-sm">{c.firstname} {c.lastname}</td>
                  <td className="px-4 py-2.5 text-xs tabular-nums hidden md:table-cell">{c.phone}</td>
                  <td className="px-4 py-2.5"><span className="md3-chip bg-[var(--md-primary-container)] text-[10px]">{c.service_name || '-'}</span></td>
                  <td className="px-4 py-2.5 text-[10px] tabular-nums hidden lg:table-cell">{c.mac || c.maccm}</td>
                  <td className="px-4 py-2.5 text-[10px] tabular-nums hidden lg:table-cell">{c.staticipcpe || c.staticipcm}</td>
                  <td className="px-4 py-2.5"><span className={`md3-chip text-[10px] ${c.enableuser === 1 ? 'badge-completed' : 'badge-rejected'}`}>{c.enableuser === 1 ? 'Active' : 'Expired'}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {data.pages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1} className="md3-outlined-btn text-xs py-1.5 px-3 disabled:opacity-40"><CaretLeft size={14} /> Prev</button>
          <span className="text-xs tabular-nums">Page {page} of {data.pages}</span>
          <button onClick={() => setPage(Math.min(data.pages, page + 1))} disabled={page >= data.pages} className="md3-outlined-btn text-xs py-1.5 px-3 disabled:opacity-40">Next <CaretRight size={14} /></button>
        </div>
      )}
    </div>
  );
}

function OLTUsersTab({ onTotalChange }) {
  const [data, setData] = useState({ total: 0, page: 1, pages: 0, data: [] });
  const [stats, setStats] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [zoneFilter, setZoneFilter] = useState('all');
  const [oltFilter, setOltFilter] = useState('all');
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 50 });
      if (search) params.append('search', search);
      if (zoneFilter !== 'all') params.append('zone', zoneFilter);
      if (oltFilter !== 'all') params.append('olt', oltFilter);
      const res = await fetch(`${API}/olt-users?${params}`);
      if (res.ok) { const next = await res.json(); setData(next); if (onTotalChange) onTotalChange(next.total || 0); }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [page, zoneFilter, oltFilter]);
  useEffect(() => {
    fetch(`${API}/olt-users/stats`).then(r => r.json()).then(setStats).catch(() => {});
    fetch(`${API}/olt-users/zones`).then(r => r.json()).then(setZones).catch(() => {});
  }, []);

  const handleSearch = (e) => { e.preventDefault(); setPage(1); load(); };

  const BRAND_CLR = {
    'MyanmarLink': 'bg-[#E3F2FD] text-[#1565C0]',
    'BurmaNet': 'bg-[#FFEBEE] text-[#C62828]',
    'OceanNet': 'bg-[#E0F2F1] text-[#00695C]',
    'MAC/Device': 'bg-[#F3E5F5] text-[#6A1B9A]',
    'PreOrder': 'bg-[#FFF8E1] text-[#F57F17]'
  };

  return (
    <div className="space-y-4">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="rounded-xl border border-[#BBDEFB] bg-[#E3F2FD] p-4 text-center">
            <WifiHigh size={24} weight="fill" className="mx-auto mb-1 text-[#1565C0]" />
            <p className="text-2xl font-bold text-[#1565C0] tabular-nums">{stats.total_users?.toLocaleString()}</p>
            <p className="text-[10px] text-[#1565C0]">Total OLT Users</p>
          </div>
          <div className="rounded-xl border border-[#C8E6C9] bg-[#E8F5E9] p-4 text-center">
            <MapPin size={24} weight="fill" className="mx-auto mb-1 text-[#2E7D32]" />
            <p className="text-2xl font-bold text-[#2E7D32] tabular-nums">{stats.zones?.length || 0}</p>
            <p className="text-[10px] text-[#2E7D32]">Zones/Townships</p>
          </div>
          <div className="rounded-xl border border-[#FFE082] bg-[#FFF8E1] p-4 text-center">
            <p className="text-2xl font-bold text-[#F57F17] tabular-nums">{stats.olts?.length || 0}</p>
            <p className="text-[10px] text-[#F57F17]">OLTs</p>
          </div>
          <div className="rounded-xl border border-[#CE93D8] bg-[#F3E5F5] p-4 text-center">
            <p className="text-2xl font-bold text-[#6A1B9A] tabular-nums">{stats.top_links?.length || 0}</p>
            <p className="text-[10px] text-[#6A1B9A]">Main Links (FAT)</p>
          </div>
        </div>
      )}

      {/* Brand Breakdown */}
      {stats?.brands && (
        <div className="flex flex-wrap gap-2">
          {stats.brands.map(b => (
            <span key={b.brand} className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium ${BRAND_CLR[b.brand] || 'bg-gray-100 text-gray-700'}`}>
              {b.brand}: {b.count.toLocaleString()}
            </span>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <form onSubmit={handleSearch} className="relative flex-1 max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--md-on-surface-variant)]" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search user name, main link, ODB..." className="md3-input pl-9 h-10" />
        </form>
        <Select value={zoneFilter} onValueChange={v => { setZoneFilter(v); setPage(1); }}>
          <SelectTrigger className="w-44 bg-[var(--md-surface-1)] border-[var(--md-outline-variant)] rounded-xl h-10"><SelectValue placeholder="All Zones" /></SelectTrigger>
          <SelectContent className="bg-white max-h-60"><SelectItem value="all">All Zones</SelectItem>{zones.map(z => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={oltFilter} onValueChange={v => { setOltFilter(v); setPage(1); }}>
          <SelectTrigger className="w-36 bg-[var(--md-surface-1)] border-[var(--md-outline-variant)] rounded-xl h-10"><SelectValue placeholder="All OLTs" /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="all">All OLTs</SelectItem>{(stats?.olts || []).map(o => <SelectItem key={o.olt} value={o.olt}>{o.olt} ({o.count})</SelectItem>)}</SelectContent>
        </Select>
        <span className="text-xs text-[var(--md-on-surface-variant)] tabular-nums">{data.total.toLocaleString()} users</span>
      </div>

      {/* Zone Breakdown Table */}
      {stats?.zones && stats.zones.length > 0 && (
        <div className="md3-card overflow-hidden">
          <div className="bg-[#1565C0] text-white px-4 py-2 text-xs font-medium">Zone / Township Breakdown</div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr className="bg-[var(--md-surface-1)]">
                <th className="text-left text-[10px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2">Zone</th>
                <th className="text-right text-[10px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2">Users</th>
                <th className="text-left text-[10px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2">Distribution</th>
              </tr></thead>
              <tbody>
                {stats.zones.map(z => (
                  <tr key={z.zone} className="border-t border-[var(--md-outline-variant)] hover:bg-[var(--md-surface-1)] cursor-pointer" onClick={() => { setZoneFilter(z.zone); setPage(1); }}>
                    <td className="px-4 py-2 text-sm font-medium text-[#1565C0]">{z.zone}</td>
                    <td className="px-4 py-2 text-sm text-right tabular-nums font-medium">{z.count.toLocaleString()}</td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-[#E3F2FD] rounded-full overflow-hidden">
                          <div className="h-full bg-[#1565C0] rounded-full" style={{ width: `${(z.count / stats.total_users * 100).toFixed(1)}%` }} />
                        </div>
                        <span className="text-[10px] tabular-nums text-[var(--md-on-surface-variant)] w-10 text-right">{(z.count / stats.total_users * 100).toFixed(1)}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="md3-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="bg-[var(--md-surface-1)]">
              <th className="text-left text-[10px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2.5">OLT</th>
              <th className="text-center text-[10px] font-medium text-[var(--md-on-surface-variant)] px-2 py-2.5">Board</th>
              <th className="text-center text-[10px] font-medium text-[var(--md-on-surface-variant)] px-2 py-2.5">Port</th>
              <th className="text-left text-[10px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2.5">Name</th>
              <th className="text-left text-[10px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2.5">Main Link</th>
              <th className="text-left text-[10px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2.5 hidden md:table-cell">ODB/Splitter</th>
              <th className="text-left text-[10px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2.5">Zone</th>
              <th className="text-left text-[10px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2.5 hidden lg:table-cell">Brand</th>
            </tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={8} className="text-center py-8"><div className="w-6 h-6 border-2 border-[var(--md-primary)] border-t-transparent rounded-full animate-spin mx-auto" /></td></tr> :
              data.data.length === 0 ? <tr><td colSpan={8} className="text-center py-8 text-sm text-[var(--md-on-surface-variant)]">No OLT users found</td></tr> :
              data.data.map((u, i) => (
                <tr key={i} className="border-t border-[var(--md-outline-variant)] hover:bg-[var(--md-surface-1)]">
                  <td className="px-4 py-2 text-xs font-medium text-[#1565C0]">{u.olt}</td>
                  <td className="px-2 py-2 text-xs text-center tabular-nums">{u.board}</td>
                  <td className="px-2 py-2 text-xs text-center tabular-nums">{u.port}</td>
                  <td className="px-4 py-2 text-sm font-medium">{u.name}</td>
                  <td className="px-4 py-2 text-xs text-[#6A1B9A]">{u.main_link_name}</td>
                  <td className="px-4 py-2 text-xs hidden md:table-cell">{u.odb_splitter}</td>
                  <td className="px-4 py-2"><span className="md3-chip bg-[#E3F2FD] text-[#1565C0] text-[10px]">{u.zone}</span></td>
                  <td className="px-4 py-2 hidden lg:table-cell"><span className={`md3-chip text-[10px] ${BRAND_CLR[u.detected_brand] || 'bg-gray-100'}`}>{u.detected_brand}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {data.pages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1} className="md3-outlined-btn text-xs py-1.5 px-3 disabled:opacity-40"><CaretLeft size={14} /> Prev</button>
          <span className="text-xs tabular-nums">Page {page} of {data.pages}</span>
          <button onClick={() => setPage(Math.min(data.pages, page + 1))} disabled={page >= data.pages} className="md3-outlined-btn text-xs py-1.5 px-3 disabled:opacity-40">Next <CaretRight size={14} /></button>
        </div>
      )}
    </div>
  );
}

export default function CustomersPage() {
  const [tab, setTab] = useState('dma');
  const [oltTotal, setOltTotal] = useState(0);
  const navigate = useNavigate();
  return (
    <div className="space-y-4" data-testid="customers-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-[var(--md-on-surface)]">Customers</h2>
          <p className="text-xs text-[var(--md-on-surface-variant)]">Customer accounts, service information, and support history.</p>
        </div>
        <button onClick={() => navigate('/customers/call-center-report')} className="md3-filled-btn flex items-center gap-2 text-sm" data-testid="call-center-wifi-report-link">
          <Headset size={16} weight="bold" /> Call Center Wi-Fi Report
        </button>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-[var(--md-outline-variant)] rounded-xl p-1 flex-wrap">
          <TabsTrigger value="dma" className="rounded-lg data-[state=active]:bg-[var(--md-primary-container)]">DMA Customers</TabsTrigger>
          <TabsTrigger value="olt" className="rounded-lg data-[state=active]:bg-[var(--md-primary-container)]">OLT Users ({oltTotal.toLocaleString()})</TabsTrigger>
        </TabsList>
        <TabsContent value="dma" className="mt-4"><DMACustomersTab /></TabsContent>
        <TabsContent value="olt" className="mt-4"><OLTUsersTab onTotalChange={setOltTotal} /></TabsContent>
      </Tabs>
    </div>
  );
}
